// CursorEditorCellPxPreference — カーソルエディタ (24×24 グリッド) の 1 マス
// サイズ (px) を localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景・設計は `previewSizePreference.ts` と同型 (プレビュー窓の resize 永続化と
// 同じ課題: 値は enum でなく数値 px、絶対範囲 clamp は保存時/読み出し時、viewport
// に応じた実効上限は呼び出し側 `useCursorEditorCellPxResize.ts` が別途適用する).
//
// Tests: cursorEditorCellPxPreference.test.ts (storage-injection style,
// previewSizePreference.test.ts と同流儀).

import {
  clampCursorEditorCellPx,
  DEFAULT_CURSOR_EDITOR_CELL_PX,
} from '../presentation/layouts/shared/cursorEditorCellPx.js';

/** localStorage キー (= 外から観察可能な "user data" なので値を固定). */
export const CURSOR_EDITOR_CELL_PX_KEY = 'oekaki:cursor-editor-cell-px';

/**
 * Minimal Storage shape this module uses (previewSizePreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CursorEditorCellPxPreferenceStore {
  /**
   * 現在の 1 マスサイズ (px) を読み出し. 不在 / 不正 / storage 不可 →
   * `DEFAULT_CURSOR_EDITOR_CELL_PX`. 取得値は常に絶対範囲へ clamp 済 (viewport
   * fit clamp は呼び出し側の責務).
   */
  get(): number;
  /** 1 マスサイズ (px) を保存. storage 不可なら no-op (例外を throw しない). */
  set(px: number): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * previewSizePreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:cursor-editor-cell-px__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateCursorEditorCellPxPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns DEFAULT_CURSOR_EDITOR_CELL_PX, setter
   * silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readCellPx = (storage: StorageLike): number => {
  try {
    const raw = storage.getItem(CURSOR_EDITOR_CELL_PX_KEY);
    if (raw === null) return DEFAULT_CURSOR_EDITOR_CELL_PX;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return DEFAULT_CURSOR_EDITOR_CELL_PX;
    return clampCursorEditorCellPx(parsed);
  } catch {
    return DEFAULT_CURSOR_EDITOR_CELL_PX;
  }
};

const writeCellPx = (storage: StorageLike, px: number): void => {
  try {
    storage.setItem(CURSOR_EDITOR_CELL_PX_KEY, String(clampCursorEditorCellPx(px)));
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `CursorEditorCellPxPreferenceStore` のインスタンスを構築. storage が
 * unavailable な環境 (SSR / Safari private browsing 厳格モード) でも getter は
 * default を返すだけで、setter は no-op (= 例外で UI が壊れることはない).
 */
export const createCursorEditorCellPxPreferenceStore = (
  opts: CreateCursorEditorCellPxPreferenceStoreOptions = {},
): CursorEditorCellPxPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_CURSOR_EDITOR_CELL_PX;
      return readCellPx(storage);
    },
    set(px) {
      if (storage === null) return;
      writeCellPx(storage, px);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(CURSOR_EDITOR_CELL_PX_KEY);
      } catch {
        // ignore
      }
    },
  };
};
